import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import useAssessmentStore from "./assessmentStore";
import { useTheme } from '@mui/material/styles';

import { useSearchParams } from "react-router-dom";
import { useLoading, useSnackbar, useServices } from "contexts";
import { useNavigateWithClient } from "hooks";
import {
  generateFollowupQuestions,
  getRandomPathwayQuestions,
  getRandomQuestionsByTemplateId
} from 'services';
import { AssessmentStates, Interviewers, interviewerStateTags, QuestionStates } from "./constants";
import { makeStyles } from "@mui/styles";
import Chip from "components/Chip";
import { templateTypes } from "components/PerformanceReview/constants";
import { Divider } from "@mui/material";
import MicTestAnimation from "components/MicTestAnimation";
import { textToSpeech } from "utils/speech";

const useStyles = makeStyles(theme => ({
  questionRoot: {
    display: 'flex',
    gap: theme.spacing(3),
    alignItems: 'center'
  },
  questionContainer: {
    overflow: 'auto',
    height: '100%',
    minHeight: '220px',
    margin: theme.spacing(3, 0),
    // '&::-webkit-scrollbar': {
    //   width: '4px',
    //   height: '4px',
    //   backgroundColor: theme.palette.neutral['clr-100']
    // },
    // '&::-webkit-scrollbar-thumb': {
    //   backgroundColor: theme.palette.neutral['clr-400']
    // },
    // '&::-webkit-scrollbar-track': {
    //   backgroundColor: '#F0F5FA'
    // }
  },
  question: {
    textAlign: 'left',
  },
  interviewerContainer: {
    height: '60px', width: '100%',
    display: 'flex', alignItems: 'center',
    gap: theme.spacing(3)
  },
  interviewer: {
    display: 'flex', alignItems: 'center',
    gap: theme.spacing(3),
    height: '100%'
  },
  animatedTag: {
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
    ,
  },
  quesSwitchCountdown: {
    height: '44px', width: '44px',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    borderRadius: '50%',
    backgroundColor: theme.palette.warning['clr-100'],
    border: '2px solid',
    borderColor: theme.palette.warning['clr-300']
  },
  fadeUp: {
    animation: '$fadeUp 0.5s ease-in',
  },
  '@keyframes fadeUp': {
    '0%': {
      opacity: 0,
      transform: 'translateY(20px)',
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
  loaderContainer: {
    position: 'relative',
    height: '100%',
  },
  loader: {
    width: '28px',
    aspectRatio: 8,
    background: 'radial-gradient(circle closest-side,#000 90%,#0000) 0/calc(100%/3) 100% space',
    clipPath: 'inset(0 100% 0 0)',
    animation: '$l1 1s steps(4) infinite',
    position: 'absolute',
    bottom: '40%',
  },
  '@keyframes l1': {
    to: {
      clipPath: 'inset(0 -34% 0 0)',
    },
  },
}));

const InterviewerState = () => {
  const classes = useStyles();

  const isInterviewerSpeaking = useAssessmentStore(state => state.isInterviewerSpeaking);
  const questionState = useAssessmentStore(state => state.questionState);
  const questionSwitch = useAssessmentStore(state => state.questionSwitch);
  const template = useAssessmentStore(state => state.template);
  const [interviewerDetails, setInterviewerDetails] = React.useState(Interviewers.Amy)
  
  const [text, setText] = React.useState("");
  const [animate, setAnimate] = React.useState(false);

  const changeText = (text) => {
    const timer = setTimeout(() => {
      setAnimate(true);
      setText(text);
    }, 300);

    return () => clearTimeout(timer);
  };

  React.useEffect(()=>{
    setInterviewerDetails(Interviewers?.[template?.metadata?.interviewer] ?? Interviewers.Amy);

    changeText(
      isInterviewerSpeaking 
        ? interviewerStateTags.SPEAKING(interviewerDetails.name) : questionState === QuestionStates.RECORDING
          ? interviewerStateTags.RECORDING(interviewerDetails.name) :  questionSwitch
            ? interviewerStateTags.NEXT(interviewerDetails.name) : interviewerStateTags.WAITING(interviewerDetails.name)
    );
  },[isInterviewerSpeaking, questionState, questionSwitch, template, interviewerDetails]);

  React.useEffect(() => {
    if (animate) {
      const animationTimer = setTimeout(() => {
        setAnimate(false);
      }, 500);

      return () => clearTimeout(animationTimer);
    }
  }, [animate]);

  return (
    <Box mb={4}>
      <Box className={classes.interviewerContainer}>
        <Box className={classes.interviewer}>
          <img 
            src={interviewerDetails.image}
            alt="interviewer"
            style={{ height: '44px', width: '44px' }}
          />
          <Box className={`${classes.animatedTag} ${animate ? classes.fadeUp : ''}`}>
            <Typography variant="h6-medium" color='neutral.clr-700'>
              {text}
            </Typography>
            <Box className={classes.loaderContainer}>
              <Box className={classes.loader}></Box>
            </Box>
          </Box>
        </Box>
      </Box>
      <Divider style={{ width: '100%' }} />
    </Box>
  )
}

function QuestionSection() {
  const [searchParams] = useSearchParams();
  const loading = useLoading();
  const snackbar = useSnackbar();
  const service = useServices();
  const navigate = useNavigateWithClient();
  const classes = useStyles();
  const theme = useTheme();
  const audioRef = React.useRef(null);
  const typeIntervalRef = React.useRef(null);
  const [isLongContent, setIsLongContent] = React.useState(false);

  const template = useAssessmentStore(state => state.template);
  const [chunck, setChunck] = React.useState("");
  const currentQuestion = useAssessmentStore(state => state.currentQuestion);
  const questions = useAssessmentStore(state => state.questions);
  const setIsTimePaused = useAssessmentStore(state => state.setIsTimePaused);
  const setIsInterviewerSpeaking = useAssessmentStore(state => state.setIsInterviewerSpeaking);
  const isPersonalizedInterview = useAssessmentStore(state => state.isPersonalizedInterview);
  const questionState = useAssessmentStore(state => state.questionState);
  const setQuestionsViewed = useAssessmentStore(state => state.setQuestionsViewed);
  const questionsViewed = useAssessmentStore(state => state.questionsViewed);
  const question = React.useMemo(
    () => questions[currentQuestion], [currentQuestion, questions]
  );
  const currentQuestionRef = React.useRef({ current: currentQuestion });
  const [assessmentState, setAssessmentState] = useAssessmentStore(
    state => [state.assessmentState, state.setAssessmentState]);

  const [setTemplateId, setQuestions] = useAssessmentStore(
    state => [state.setTemplateId, state.setQuestions]);

  const isCopyPasteEnabled = template?.proctorSettings?.copyPasteProctoring;

  React.useEffect(() => {
    if (assessmentState === AssessmentStates.FETCHING_QUESTIONS) {
      (async () => {
        const templateId = searchParams.get("tid");

        if (templateId) {
          if (!template.type) return;
          try {
            loading.show();
            let questions = [];

            switch (template.type) {
              case 'follow_up':
                questions = await service.callService(
                  generateFollowupQuestions,
                  [...template.templatesForFollowup, templateId]
                );
                break;

              case 'pathway':
                questions = await service.callService(
                  getRandomPathwayQuestions,
                  {
                    tags: template.tags,
                    pathways: [template.pathway],
                    numberOfQuestions: template.metadata.numberOfQuestions || 1,
                    domains: [template.domain],
                    masterTemplate: template?.module?.masterModule || false
                  }
                );
                break;

              case 'practice':
              case 'assignment':
                questions = (await service.callService(
                  getRandomQuestionsByTemplateId,
                  templateId
                )).interviewQuestions;
                break;
              
              case 'personalized':
                questions = (await service.callService(
                    getRandomQuestionsByTemplateId,
                    templateId, template?.metadata?.testTime || 3600
                )).interviewQuestions;
                break;

              default:
                break;
            }

            if (questions.length <= 0) {
              snackbar.error(
                `There are no questions in this template,
                              Please add some questions.`,
              );
              navigate("/home");
              return;
            }

            setTemplateId(templateId);
            setQuestions(questions);
            setAssessmentState(AssessmentStates.STARTING);
          } catch (error) {
            console.log("DEBUG::handleBeginClick", error);
            snackbar.error(
              "Unable to begin assessment, Please try again!!"
            );
            navigate("/home");
          } finally {
            loading.hide();
          }
        } else {
          navigate("/home");
          snackbar.warn(
            "Please, Select a template to take an Interview!!"
          );
        }
      })();
    }
  }, [assessmentState, searchParams, setAssessmentState, setTemplateId, setQuestions, template]);

  React.useEffect(() => {
    currentQuestionRef.current = currentQuestion;
  }, [currentQuestion]);

  const typeText = (textToType) => {
    if (typeIntervalRef.current) {
      clearInterval(typeIntervalRef.current);
      typeIntervalRef.current = null;
    }
    setChunck('');

    // Parse HTML and build a flat list of renderable items
    const parser = new DOMParser();
    const doc = parser.parseFromString(textToType, 'text/html');

    const items = [];
    let itemIndex = 0;

    const traverse = (node, tagStack = []) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent;
        for (let i = 0; i < text.length; i++) {
          items.push({
            type: 'char',
            char: text[i],
            tags: [...tagStack],
            index: itemIndex++
          });
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const tagName = node.nodeName.toLowerCase();
        const attrs = Array.from(node.attributes)
          .map(attr => ` ${attr.name}="${attr.value}"`)
          .join('');

        const tagInfo = {
          name: tagName,
          open: `<${tagName}${attrs}>`,
          close: `</${tagName}>`,
          selfClosing: ['br', 'hr', 'img', 'input', 'meta', 'link'].includes(tagName)
        };

        if (tagInfo.selfClosing) {
          // Add self-closing tag as a separate item
          items.push({
            type: 'selfClosing',
            tag: `<${tagName}${attrs}>`,
            tags: [...tagStack],
            index: itemIndex++
          });
        } else {
          // Regular tag with potential children
          tagStack.push(tagInfo);
          node.childNodes.forEach(child => traverse(child, tagStack));
          tagStack.pop();
        }
      }
    };

    traverse(doc.body);
    let currentIndex = 0;

    typeIntervalRef.current = setInterval(() => {
      setIsInterviewerSpeaking(true);
      setIsTimePaused(true);

      if (currentIndex < items.length) {
        // Build HTML from items up to current index
        let html = '';
        let activeTagStack = [];

        for (let i = 0; i <= currentIndex; i++) {
          const item = items[i];

          if (item.type === 'selfClosing') {
            html += item.tag;
          } else if (item.type === 'char') {
            // Check if we need to open/close tags
            const currentTags = item.tags;

            // Find where tags differ
            let commonDepth = 0;
            while (
              commonDepth < Math.min(activeTagStack.length, currentTags.length) &&
              activeTagStack[commonDepth].name === currentTags[commonDepth].name
            ) {
              commonDepth++;
            }

            // Close tags that are no longer needed
            for (let j = activeTagStack.length - 1; j >= commonDepth; j--) {
              html += activeTagStack[j].close;
            }

            // Open new tags
            for (let j = commonDepth; j < currentTags.length; j++) {
              html += currentTags[j].open;
            }

            // Add the character
            html += item.char;

            activeTagStack = [...currentTags];
          }
        }

        // Close any remaining open tags
        for (let j = activeTagStack.length - 1; j >= 0; j--) {
          html += activeTagStack[j].close;
        }

        setChunck(html);
        currentIndex++;
      } else {
        setQuestionsViewed(currentQuestionRef.current, true);
        setIsInterviewerSpeaking(false);
        setIsTimePaused(false);
        clearInterval(typeIntervalRef.current);
        typeIntervalRef.current = null;
      }
    }, 60);
  };

  const reset = React.useCallback(() => {
    if (audioRef.current && !audioRef.current.paused) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  }, []);

  const toSpeech = React.useCallback(async (text = '') => {
    const interviewerDetails = Interviewers?.[template?.metadata?.interviewer] ?? Interviewers.Amy;
    reset();
    typeText(text);
    await textToSpeech({
      text,
      language: interviewerDetails.language,
      voiceId: interviewerDetails.voiceId,
      audioRef,
    });
  }, [template]);

    React.useEffect(()=>{
      if(template?._id && assessmentState === AssessmentStates.RUNNING){
        if(template.type === templateTypes.PERSONALIZED && !questionsViewed[currentQuestion]){
          toSpeech(question?.question);
        }else if(template.type === templateTypes.PRACTICE || template.type === templateTypes.ASSIGNMENT){
          // Apply typewriting animation for practice and assignment without audio
          if(!questionsViewed[currentQuestion]){
            typeText(question?.question);
          }else{
            setChunck(question?.question);
          }
        }else{
          setChunck(question?.question);
        }
      }
    },[question, template, assessmentState]);

    React.useEffect(() => {
      const checkContentLength = () => {
        const plainText = chunck.replace(/<[^>]*>/g, '');
        setIsLongContent(plainText.length > 300);
      };

      checkContentLength();
    }, [chunck]);

    return (
        <Box width='100%'>
            {
              isPersonalizedInterview && <InterviewerState/>
            }
            <Box display='flex' justifyContent='space-between'>
              <Box className={classes.questionRoot}>
                <Typography variant='h6-bold' color='primary.clr-300'>
                    Question {currentQuestion + 1} of {questions.length}
                </Typography>
                {
                  !isPersonalizedInterview && question?.type &&
                  <Chip 
                    content={`${question.type} question`}
                    bgColor={theme.palette.secondary['clr-700']}
                    color={theme.palette.shades['clr-white-900']}
                    sx={{ textTransform: 'capitalize', padding: theme.spacing(1,5) }}
                  />
                }
        </Box>
        {
          ["RECORDING"].includes(questionState) ?
            <Box>
              <MicTestAnimation start={true} />
            </Box> :
            null
        }
      </Box>
      <Box className={classes.questionContainer} sx={{ maxHeight: isLongContent ? '280px' : '220px' }}>
        <audio ref={audioRef}></audio>
        <Typography
          className={classes.question}
          variant={isLongContent ? 'h5-semiBold' : 'h3-semiBold'}
          style={{
            textAlign: 'left',
            ...(isCopyPasteEnabled ? {
              userSelect: 'none',
              WebkitTouchCallout: 'none',
              WebkitUserSelect: 'none',
              MozUserSelect: 'none',
              msUserSelect: 'none',
            } : {})
          }}
          dangerouslySetInnerHTML={{ __html: chunck }}
        />
      </Box>
      {/* {question?.hint && (
        <Typography variant='body01-regular' color='neutral.clr-600'>
          💡 Hint: {question?.hint}
        </Typography>
      )} */}
    </Box>
  );
}

export default QuestionSection;